import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { getTopPerformingMerchantsAction } from "../../Actions/TransactionActions";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { renderStatusBadge } from "../../utils/badges";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";

const DashboardTopTenants = ({
  showButton,
  onRemove,
  hasTransactionPermission,
  onShowSnackbar,
}) => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTopPerformingMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTopPerformingMerchantsAction();
      
      const data = response.data || [];
      setMerchants(data);
    } catch (error) {
      console.error('Error fetching merchants:', error);
      setMerchants([]);
      if (error?.response?.data?.message) {
        onShowSnackbar(error.response.data.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [onShowSnackbar]);

  useEffect(() => {
    fetchTopPerformingMerchants();
  }, [fetchTopPerformingMerchants]);

  const columns = useMemo(() => [
    {
      field: "merchantName",
      headerName: "Tenant Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "companyName",
      headerName: "Company Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "totalTransactions",
      headerName: "Total Transaction",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.totalAmount ? `CI$ ${params.row.totalAmount}` : "N/A",
    },
    {
      field: "avgTransactionAmount",
      headerName: "Avg. Transaction Amount",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.avgTransactionAmount ? `CI$ ${params.row.avgTransactionAmount}` : "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const statusMap = {
          Active: { text: "Active", status: "success" },
          Inactive: { text: "Inactive", status: "default" },
        };
        const statusCode = params.row?.status ?? "Inactive";
        const { text, status } = statusMap[statusCode] || { text: "Unknown", status: "default" };
        return renderStatusBadge({ status, title: text });
      },
    },
  ], []);

  if (!hasTransactionPermission?.canView) return null;


  return (
    <div className={`${!showButton && "my-3"}`} key="topPerformingMerchantList" style={{ width: '100%' }} >
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("topPerformingMerchantList")}
        />
      )}

      <div className="card border-rounded-2 fiter-card mt-6">
        <div className="card-body p-3 d-flex justify-content-between align-items-center">
          <h2 className="chart-title mb-0">Top Performing Tenant</h2>
          <button
            className="view-all"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/tenant")}
          >
            View All
          </button>
        </div>
      </div>

      <div style={{ width: '100%' }}>
        <DataGrid
          rows={merchants}
          columns={columns}
          autoHeight
          hideFooter
          loading={loading}
          getRowId={(row) => row.merchantId}
          components={{
            LoadingOverlay: () => (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "110px" }}>
                <CircularProgress color="primary" size={24} style={{ marginRight: 10 }} />
                Loading top performing merchants...
              </div>
            ),
            NoRowsOverlay: () => (
              <div style={{ fontWeight: "bold", textAlign: "center", padding: "20px" }}>
                No Records Found
              </div>
            ),
          }}
          sx={{
            ...DataGrid_SX,
            minHeight: 200,
          }}
        />
      </div>
    </div>
  );
};

export default DashboardTopTenants;